import { useState } from 'react';
import { FormikHelpers } from 'formik';
import * as yup from 'yup';

import FormSubmitStatus from 'components/FormSubmitStatus';
import { Button } from 'components/Input/Button';
import InputField from 'components/Input/InputField/InputField';

import { PlainTextMessage } from 'routes/Chat/Messages/PlainTextMessage';
import WithDelayedAnimation from 'routes/Chat/Messages/WithDelayedAnimation/WithDelayedAnimation';

import { formatTimer } from './utils';

import { validationSchemaCode } from '../../formValidations';
import { PHONE_VERIFICATION_TEXT } from './constants';
import { Section, StyledForm, SubmitSection } from './styles';
import { RatingContactFollowUpChallengeFormType } from './types';
import { htmlId } from 'utils/html';
import FormWrapper from 'components/FormWrapper';
import { FORM_IDS } from 'components/FormWrapper/constants';

type FormProps = {
  animate: boolean;
  initialValues: RatingContactFollowUpChallengeFormType;
  isMaxAttempts: boolean;
  twilioMessageIndex: number;
  previousCode?: string;
  resendCodeTimer: number;
  onSubmit: (
    values: RatingContactFollowUpChallengeFormType,
    helpers: FormikHelpers<RatingContactFollowUpChallengeFormType>,
  ) => void;
  onResendCode: () => void;
};

const validationSchema = yup.object({
  code: validationSchemaCode,
});

const BaseChallenge = (props: FormProps) => {
  const {
    animate,
    initialValues,
    isMaxAttempts,
    twilioMessageIndex,
    resendCodeTimer,
    onSubmit,
    onResendCode,
  } = props;

  const [lastSubmittedValues, setLastSubmittedValues] = useState<undefined | { code: string }>(
    undefined,
  );

  const handleOnSubmit = async (
    values: RatingContactFollowUpChallengeFormType,
    helpers: FormikHelpers<RatingContactFollowUpChallengeFormType>,
  ) => {
    setLastSubmittedValues(values);
    onSubmit(values, helpers);
  };

  return (
    <FormWrapper
      id={FORM_IDS.RATING_CONTACT_FOLLOW_UP_CHALLENGE}
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleOnSubmit}
    >
      {({ dirty, isValid, isValidating, isSubmitting, status, resetForm, values }) => {
        const inputDisabled = status === 'submitted';
        const resendCodeDisabled = inputDisabled || resendCodeTimer > 0;
        const submitDisabled = inputDisabled || !isValid || isValidating || isSubmitting || !dirty;

        let errorMessage;
        if (isMaxAttempts) {
          errorMessage = 'Too many attempts! Please enter the last verification code you received.';
        } else if (status && values.code !== lastSubmittedValues?.code) {
          errorMessage = null;
        } else {
          errorMessage = status;
        }

        const commonAnimationProps = { animate, showIndicator: true };

        return (
          <StyledForm>
            <Section>
              <WithDelayedAnimation relativeIndex={0} {...commonAnimationProps}>
                <PlainTextMessage text={PHONE_VERIFICATION_TEXT} />
              </WithDelayedAnimation>
              <WithDelayedAnimation relativeIndex={1} {...commonAnimationProps}>
                <InputField
                  id={`code-${twilioMessageIndex}`}
                  name='code'
                  type='text'
                  inputMode='numeric'
                  autoComplete='off'
                  placeholder='6-Digit Code'
                  label='6-Digit Code'
                  disabled={inputDisabled}
                  onInput={(e: any) => {
                    e.target.value = e.target.value.replace(/\D+/, '').slice(0, 6);
                  }}
                />
              </WithDelayedAnimation>
            </Section>
            {(!!status || isMaxAttempts) && (
              <FormSubmitStatus isSuccess={status === 'submitted'} errorMessage={errorMessage} />
            )}
            {!inputDisabled && (
              <WithDelayedAnimation relativeIndex={2} {...commonAnimationProps}>
                <SubmitSection>
                  <>
                    <Button
                      id={htmlId('rating-contact-follow-up-challenge-resend')}
                      priority='text'
                      type='button'
                      disabled={resendCodeDisabled}
                      onClick={() => {
                        resetForm({ values: { code: '' } });
                        onResendCode();
                      }}
                    >
                      Send another code
                      {resendCodeDisabled && ` (${formatTimer(resendCodeTimer, true)})`}
                    </Button>
                    <Button
                      id={htmlId('rating-contact-follow-up-challenge-submit')}
                      type='submit'
                      disabled={submitDisabled}
                    >
                      Submit
                    </Button>
                  </>
                </SubmitSection>
              </WithDelayedAnimation>
            )}
          </StyledForm>
        );
      }}
    </FormWrapper>
  );
};

export default BaseChallenge;
